import { ControlCellInput, DeviceWithInstalledPower, Direction, ProductInput } from '@e2m-customer-portal/domains';
import { ForecastPageLimitsResponse, ForecastSlice, useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, parseNumber } from '@e2m-customer-portal/shared';
import { Popover } from '@e2m-customer-portal/ui';
import { EditIcon, ErrorIcon, WarningIcon } from 'assets/icons';
import clsx from 'clsx';
import { addHours, Interval, isBefore, isToday, isWeekend, subHours } from 'date-fns';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DecoratedControlCellProps {
  device: DeviceWithInstalledPower;
  deadlineDate?: Date;
  products: Array<InputFieldType>;
  selectedProducts: Set<InputFieldType>;
  slice: ForecastSlice;
  limits: ForecastPageLimitsResponse;
  cellIndex: {
    row: number;
    column: number;
  };
  readOnly?: boolean;
  resetTrigger?: boolean;
  isHighlighted: boolean;
  overviewMap: Map<string, ProductInput[][][]>;
  editedSliceProducts: Map<string, Set<InputFieldType>>;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, ProductInput[][][]>>>;
  setEditedSliceProducts: React.Dispatch<React.SetStateAction<Map<string, Set<InputFieldType>>>>;
  setErroneousSliceProducts: React.Dispatch<React.SetStateAction<Map<string, Set<InputFieldType>>>>;
  switchFocus: (sourceRow: number, sourceColumn: number, sourceProductIndex: number, direction?: Direction) => boolean;
  saveChanges: () => void;
}

export function DecoratedControlCell({
  device,
  deadlineDate,
  products,
  selectedProducts,
  slice,
  limits,
  cellIndex,
  readOnly,
  resetTrigger,
  isHighlighted,
  overviewMap,
  editedSliceProducts,
  setRevokeDowntimeInterval,
  setIsHighlighted,
  setOverviewMap,
  setEditedSliceProducts,
  setErroneousSliceProducts,
  switchFocus,
  saveChanges,
}: Readonly<DecoratedControlCellProps>): ReactElement<DecoratedControlCellProps> {
  const { t } = useTranslation();
  const { code: locale } = useLocale();
  const isAfterDeadline = deadlineDate && isBefore(slice.date, deadlineDate);
  const isAfterDowntimeDeadline = isBefore(slice.date, subHours(new Date(), 4));

  const convertProductValue = (product: InputFieldType) =>
    convertMeasure(
      slice.fields.find((value) => value.type === product)?.value ?? {
        value: null,
        unit: 'kW',
      },
      'kW',
    ).value;

  const [values, setValues] = useState<Map<InputFieldType, string>>(
    new Map(
      slice.fields.map((field) => [
        field.type,
        convertMeasure(field.value, 'kW').value?.toLocaleString(locale, { useGrouping: false }) ?? '',
      ]),
    ),
  );

  const [errors, setErrors] = useState<Map<InputFieldType, string | undefined>>(
    new Map(slice.fields.map((field) => [field.type, undefined])),
  );

  const setValue = (product: InputFieldType) => (value?: string) => {
    setValues((values) => {
      const updatedValues = new Map(values);
      updatedValues.set(product, value ?? '');
      return updatedValues;
    });
  };

  const setError = (product: InputFieldType) => (error?: string) => {
    setErrors((errors) => {
      const updatedErrors = new Map(errors);
      updatedErrors.set(product, error);
      return updatedErrors;
    });
  };

  useEffect(() => {
    for (const { type: product } of slice.fields) {
      setValue(product)(convertProductValue(product)?.toLocaleString(locale, { useGrouping: false }) ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slice.hasDowntime, slice.fields]);

  useEffect(() => {
    const parsedValues = new Map([...values].map(([product, value]) => [product, parseNumber(value)]));

    setIsHighlighted(false);
    setEditedSliceProducts((editedSliceProducts) => {
      const slices = new Map(editedSliceProducts);
      for (const product of selectedProducts) {
        const parsedValue = parsedValues.get(product) ?? Number.NaN;
        const productValue = convertProductValue(product);

        if (!isNaN(parsedValue) && parsedValue !== productValue) {
          slices.set(
            slice.id,
            slices.has(slice.id) ? new Set([...slices.get(slice.id)!, product]) : new Set([product]),
          );
        } else if (
          slices.get(slice.id)?.has(product) &&
          (parsedValue === productValue || (productValue === null && values.get(product) === ''))
        ) {
          if (slices.get(slice.id)?.size === 1) {
            slices.delete(slice.id);
          } else {
            slices.get(slice.id)?.delete(product);
          }
        }
      }
      return slices;
    });
    setErroneousSliceProducts((erroneousSliceProducts) => {
      const slices = new Map(erroneousSliceProducts);
      for (const product of selectedProducts) {
        const error = errors.get(product);

        if (error && !erroneousSliceProducts.get(slice.id)?.has(product)) {
          slices.set(
            slice.id,
            slices.has(slice.id) ? new Set([...slices.get(slice.id)!, product]) : new Set([product]),
          );
        } else if (slices.get(slice.id)?.has(product) && !error && erroneousSliceProducts.get(slice.id)?.has(product)) {
          if (slices.get(slice.id)?.size === 1) {
            slices.delete(slice.id);
          } else {
            slices.get(slice.id)?.delete(product);
          }
        }
      }
      return slices;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    setIsHighlighted(false);
    if (slice.fields.length < 1) {
      for (const product of products) {
        setError(product)();
        setValue(product)('');
      }
    }
    for (const { type: product } of slice.fields) {
      setError(product)();
      setValue(product)(convertProductValue(product)?.toLocaleString(locale, { useGrouping: false }) ?? '');
    }

    // We carefully remove each slice from the set, because simply replacing each set with an
    // empty one will fire when new tables are expanded and useEffect triggers on render,
    // even if other tables are still in an edited state.
    setEditedSliceProducts((editedSliceProducts) => {
      const slices = new Map(editedSliceProducts);
      slices.delete(slice.id);
      return slices;
    });
    setErroneousSliceProducts((erroneousSliceProducts) => {
      const slices = new Map(erroneousSliceProducts);
      slices.delete(slice.id);
      return slices;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetTrigger]);

  const [downtimeRef, setDowntimeRef] = useState<HTMLDivElement | null>();

  return (
    <td className="relative p-0">
      {slice.hasDowntime && !editedSliceProducts.has(slice.id) ? (
        <div
          ref={setDowntimeRef}
          className={clsx(
            'absolute inset-0 box-border border-0 border-b border-solid',
            products.length >= 2
              ? 'border-blue-grey-100'
              : 'border-grey-100 group-last-of-type/last-row:border-blue-grey-100',
          )}
        >
          <button
            className={clsx(
              'm-0 box-border flex size-full items-center justify-center border-0 border-l border-solid border-grey-100 bg-transparent p-0',
              isAfterDowntimeDeadline || readOnly ? 'opacity-50' : 'cursor-pointer',
            )}
            onClick={() => {
              if (!isAfterDeadline) {
                setEditedSliceProducts((editedSliceProducts) =>
                  new Map(editedSliceProducts).set(
                    slice.id,
                    new Set(
                      products.filter(
                        (product) => ![InputFieldType.AFRR_NEGATIVE, InputFieldType.AFRR_POSITIVE].includes(product),
                      ),
                    ),
                  ),
                );
                setTimeout(() => switchFocus(cellIndex.row, cellIndex.column, 0));
              } else if (isAfterDeadline && !isAfterDowntimeDeadline) {
                setRevokeDowntimeInterval({ start: slice.date, end: addHours(slice.date, 4) });
              }
            }}
          >
            <WarningIcon data-testid="warning-icon" />
            <span className="sr-only">
              {t('MARKET_LOCATION_AVAILABILITY_OUTAGE_DETAILS', { start: slice.date, end: addHours(slice.date, 4) })}
            </span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col">
          {products.map((product, productIndex) => {
            const error = errors.get(product);

            return (
              <div
                key={product}
                className={clsx(
                  'group/cell relative box-border border-0 border-b border-solid text-center outline-2 -outline-offset-1',
                  products.length >= 2 && productIndex === products.length - 1
                    ? 'border-blue-grey-100'
                    : 'border-grey-100',
                  {
                    'bg-grey-50': (productIndex + (products.length % 2 ? cellIndex.row : 0)) % 2 === 0,
                    'group-last-of-type/last-row:border-blue-grey-100':
                      products.length < 2 && productIndex === products.length - 1,
                    'z-20 outline outline-error-500': error,
                    'focus-within:z-30 focus-within:outline focus-within:outline-green-200': !readOnly && !error,
                  },
                )}
              >
                <div
                  className={clsx('box-border border-0 border-l border-solid border-grey-100 text-sm', {
                    'bg-blue-grey-200/5': isWeekend(subHours(slice.date, slice.date.getTimezoneOffset() / 60)),
                  })}
                >
                  <div className={clsx({ 'bg-green-50/40': isToday(slice.date) })}>
                    <ControlCellInput
                      device={device}
                      slice={slice}
                      limits={limits}
                      product={product}
                      productIndex={productIndex}
                      cellIndex={cellIndex}
                      value={values.get(product) ?? ''}
                      disabled={isAfterDeadline || !selectedProducts.has(product)}
                      readOnly={readOnly}
                      setValue={setValue(product)}
                      setError={setError(product)}
                      setOverviewMap={setOverviewMap}
                      switchFocus={switchFocus}
                      saveChanges={saveChanges}
                    />
                  </div>
                </div>

                <div
                  className={clsx(
                    'pointer-events-none absolute inset-y-0 right-0 z-20 flex aspect-[1.5] items-center justify-center transition-colors',
                    {
                      'bg-error-500': error,
                    },
                  )}
                >
                  {error ? (
                    <ErrorIcon className="fill-white" />
                  ) : (
                    !error &&
                    editedSliceProducts.get(slice.id)?.has(product) && (
                      <>
                        {isHighlighted && (
                          <div className="animate-ping absolute right-2 top-0.5 size-5 rounded-full bg-error-50/50" />
                        )}
                        <EditIcon className="text-black/30 group-focus-within/cell:fill-green-200" />
                      </>
                    )
                  )}
                </div>
                {error && (
                  <Popover
                    baseElement={
                      overviewMap.get(device.id)?.at(cellIndex.row)?.at(cellIndex.column)?.at(productIndex)?.input
                    }
                    error={error}
                  />
                )}
                {slice.hasDowntime &&
                  editedSliceProducts.has(slice.id) &&
                  overviewMap.get(device.id)?.at(cellIndex.row)?.at(cellIndex.column)?.at(0)?.input && (
                    <Popover
                      baseElement={
                        overviewMap.get(device.id)?.at(cellIndex.row)?.at(cellIndex.column)?.at(0)?.input ?? downtimeRef
                      }
                      info={<p className="m-0">{t('DOWNTIME_CANCELED')}</p>}
                    />
                  )}
              </div>
            );
          })}
        </div>
      )}
    </td>
  );
}
